import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useAuth } from '@/hooks'
import { signupSchema, type SignupData } from '@/lib/validation'
import { getAuthErrorMessage } from '@/lib/errors'
import { useNavigate } from 'react-router-dom'

export const SignupForm = () => {
  const { signUp, signInWithGoogle } = useAuth()
  const navigate = useNavigate()
  const [authError, setAuthError] = useState<string>('')

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SignupData>({
    resolver: zodResolver(signupSchema),
  })

  const onSubmit = async (data: SignupData) => {
    try {
      setAuthError('')
      await signUp(data)
      navigate('/today')
    } catch (error: any) {
      setAuthError(getAuthErrorMessage(error))
    }
  }

  const handleGoogleSignIn = async () => {
    try {
      setAuthError('')
      await signInWithGoogle()
      navigate('/today')
    } catch (error: any) {
      setAuthError(getAuthErrorMessage(error))
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div>
        <label
          htmlFor="displayName"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Name
        </label>
        <input
          {...register('displayName')}
          type="text"
          id="displayName"
          className="input"
          placeholder="Your name"
        />
        {errors.displayName && (
          <p className="mt-1 text-sm text-red-600">
            {errors.displayName.message}
          </p>
        )}
      </div>

      <div>
        <label
          htmlFor="email"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Email
        </label>
        <input
          {...register('email')}
          type="email"
          id="email"
          className="input"
          placeholder="you@example.com"
        />
        {errors.email && (
          <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
        )}
      </div>

      <div>
        <label
          htmlFor="password"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Password
        </label>
        <input
          {...register('password')}
          type="password"
          id="password"
          className="input"
          placeholder="••••••••"
        />
        {errors.password && (
          <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
        )}
      </div>

      {authError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{authError}</p>
        </div>
      )}

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full btn-primary"
      >
        {isSubmitting ? 'Creating account...' : 'Sign Up'}
      </button>

      <div className="flex items-center gap-3">
        <div className="h-px bg-gray-200 flex-1" />
        <span className="text-xs text-gray-500">or</span>
        <div className="h-px bg-gray-200 flex-1" />
      </div>

      <button
        type="button"
        onClick={handleGoogleSignIn}
        className="w-full btn-secondary"
      >
        Continue with Google
      </button>
    </form>
  )
}
